/**
 * FASE B (B3) — Vista dettaglio cliente unificato con timeline cross-ramo.
 */
import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import {
  User, Phone, Mail, MapPin, ArrowLeft, Clock, ShieldAlert, AlertCircle,
  Loader2, FileText, CheckCircle2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAMO_ICON = {
  convenzioni_auto: { label: "RCA", color: "bg-emerald-100 text-emerald-800" },
  d_o: { label: "D&O", color: "bg-indigo-100 text-indigo-800" },
  cyber: { label: "Cyber", color: "bg-fuchsia-100 text-fuchsia-800" },
  fine_arts: { label: "Fine Arts", color: "bg-amber-100 text-amber-800" },
};

export default function AdminClienteUnificatoPage() {
  const { cliente_id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/clienti-unificati/${cliente_id}`,
                { withCredentials: true })
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || e.message));
  }, [cliente_id]);

  if (error) {
    return (
      <div className="p-6" data-testid="cliente-not-found">
        <AlertCircle className="w-6 h-6 text-red-600 mb-2" />
        <p className="text-red-800">Errore: {error}</p>
        <Link to="/admin/cockpit" className="text-sm underline">← Torna alle richieste</Link>
      </div>
    );
  }
  if (!data) {
    return <div className="p-6"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const c = data.cliente;
  const nome = [c.nome_primario, c.cognome_primario].filter(Boolean).join(" ") || "Cliente";
  const richieste = (c.richieste || []).slice().sort(
    (a, b) => String(b.submitted_at).localeCompare(String(a.submitted_at)));

  return (
    <div className="p-6 max-w-5xl mx-auto" data-testid="admin-cliente-unificato-page">
      <Link to="/admin/cockpit" className="text-sm text-[#4A5E51] hover:underline inline-flex items-center gap-1 mb-4">
        <ArrowLeft className="w-4 h-4" /> Torna alle richieste
      </Link>

      {/* Header cliente */}
      <div className="bg-gradient-to-br from-[#F8F4ED] to-white border border-[#E5E2DB] rounded-2xl p-6 mb-5">
        <div className="flex items-start gap-4">
          <div className="w-14 h-14 rounded-full bg-[#163321] flex items-center justify-center shrink-0">
            <User className="w-7 h-7 text-[#C9A448]" />
          </div>
          <div className="flex-1">
            <h1 className="font-heading text-2xl text-[#0F1F14]">{nome}</h1>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-3 text-sm">
              <div className="flex items-center gap-2">
                <ShieldAlert className="w-4 h-4 text-slate-500" />
                <span className="font-mono">{c.codice_fiscale || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-500" />
                <span>{c.telefono_primario || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-500" />
                <span>{c.email_primaria || "—"}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-500" />
                <span>{c.comune_primario || "—"} {c.provincia_primaria || ""}</span>
              </div>
            </div>
            <div className="mt-4">
              <span className="inline-flex items-center gap-2 bg-[#163321] text-[#C9A448] px-3 py-1 rounded-full text-sm font-medium">
                <FileText className="w-4 h-4" /> {c.num_richieste_totali} richieste totali
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Storico contatti alternativi */}
      {((c.telefoni_storici?.length > 1) || (c.emails_storiche?.length > 1)
         || (c.comuni_storici?.length > 0)) && (
        <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 mb-5">
          <h3 className="font-heading text-lg mb-3">Storico contatti</h3>
          <div className="space-y-2 text-xs">
            {(c.telefoni_storici || []).map((t, i) => (
              <div key={i}>📞 {t.value} <span className="text-slate-500">— {t.source_module} · {t.added_at?.slice(0, 10)}</span></div>
            ))}
            {(c.emails_storiche || []).map((t, i) => (
              <div key={i}>✉️ {t.value} <span className="text-slate-500">— {t.source_module} · {t.added_at?.slice(0, 10)}</span></div>
            ))}
            {(c.comuni_storici || []).map((t, i) => (
              <div key={i}>🏙 {t.comune} ({t.provincia}) <span className="text-slate-500">— {t.source_module} · {t.added_at?.slice(0, 10)}</span></div>
            ))}
          </div>
        </div>
      )}

      {/* Timeline richieste */}
      <div className="bg-white border border-[#E5E2DB] rounded-2xl p-5 mb-5">
        <h3 className="font-heading text-lg mb-4 inline-flex items-center gap-2">
          <Clock className="w-5 h-5 text-[#163321]" />
          Timeline richieste cross-ramo
        </h3>
        <div className="space-y-3">
          {richieste.map((r, i) => {
            const meta = RAMO_ICON[r.module] || { label: r.module, color: "bg-slate-100 text-slate-700" };
            return (
              <div key={i} data-testid={`timeline-item-${i}`}
                    className="flex items-start gap-3 border-l-2 border-[#D9C49A] pl-4 pb-3 relative">
                <span className={`absolute -left-[7px] top-1 w-3 h-3 rounded-full bg-[#163321]`} />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${meta.color}`}>{meta.label}</span>
                    <span className="text-[11px] text-slate-500">
                      {r.submitted_at ? new Date(r.submitted_at).toLocaleString("it-IT") : "—"}
                    </span>
                    {r.status && (
                      <span className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded uppercase tracking-wide">
                        {r.status}
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-slate-800">
                    {r.tipo || "richiesta"} · <span className="font-mono text-[11px]">{r.lead_id}</span>
                  </div>
                </div>
                {(r.module === "d_o" || r.module === "cyber" || r.module === "fine_arts") && (
                  <a
                    href={
                      r.module === "d_o"
                        ? `${API}/admin/do/questionnaires/${r.lead_id}/pdf-unified-admin`
                        : r.module === "cyber"
                        ? `${API}/admin/cyber/pratiche/${r.lead_id}/pdf-unified-admin`
                        : `${API}/admin/fine-arts/pratiche/${r.lead_id}/pdf-unified-admin`
                    }
                    target="_blank" rel="noreferrer"
                    data-testid={`pdf-admin-timeline-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] font-medium bg-[#163321] text-white px-2 py-1 rounded hover:bg-[#1F3A28] whitespace-nowrap"
                    title="Scarica PDF Unificato ADMIN">
                    <FileText className="w-3 h-3" /> PDF Admin
                  </a>
                )}
              </div>
            );
          })}
          {richieste.length === 0 && (
            <p className="text-sm text-slate-500">Nessuna richiesta storica.</p>
          )}
        </div>
      </div>

      {/* Discrepancies */}
      {(c.data_discrepancies?.length > 0) && (
        <div className="bg-amber-50 border border-amber-300 rounded-2xl p-5 mb-5"
              data-testid="discrepancies-block">
          <h3 className="font-heading text-lg mb-3 inline-flex items-center gap-2 text-amber-900">
            <AlertCircle className="w-5 h-5" /> Divergenze rilevate ({c.data_discrepancies.length})
          </h3>
          <ul className="text-xs space-y-2">
            {c.data_discrepancies.map((d, i) => (
              <li key={i}>
                <strong>{d.campo}</strong>: <span className="line-through text-slate-500">{d.valore_storico}</span> → <span className="text-amber-900">{d.valore_nuovo}</span>
                <span className="text-slate-500 ml-2">· {d.module} · {d.timestamp?.slice(0, 10)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Task correlati */}
      {(data.task_correlati?.length > 0) && (
        <div className="bg-red-50 border border-red-300 rounded-2xl p-5"
              data-testid="tasks-block">
          <h3 className="font-heading text-lg mb-3 inline-flex items-center gap-2 text-red-800">
            🔥 Task follow-up attivi ({data.task_correlati.length})
          </h3>
          {data.task_correlati.map((t) => (
            <div key={t.id} className="text-sm border-t border-red-200 pt-2 mt-2 first:border-0 first:pt-0 first:mt-0">
              <div className="font-medium">{t.tipo}</div>
              <div className="text-xs text-slate-600">
                Priorità: {t.priorita} · Status: {t.status} · Trigger: {t.trigger_reason}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
